import {Injectable, Injector} from "@angular/core";
import {BaseService} from "@shared/services/base.service";
import {HttpClient} from "@angular/common/http";
import {Observable} from "rxjs";
import {catchError, map} from "rxjs/operators";
import * as moment from 'moment';

/**
 * Interface for Provider entity
 */
export interface Provider {
	id: number;
	name: string;
	carrierWeighting?: number | null;
}

/**
 * Interface for Product entity
 */
export interface Product {
	id: number;
	name: string;
}

/**
 * Interface for Provider-Product relationship with gross rate override
 */
export interface ProviderProduct {
	id: number;
	providerId: number;
	provider?: Provider;
	productId: number;
	product?: Product;
	grossPercentRateOverride?: number | null;
	creatorUserId?: number | null;
	creationTime?: Date;
}

/**
 * Interface for updating provider-product gross rate override
 */
export interface UpdateProviderProductGrossRateRequest {
	grossPercentRateOverride: number | null;
}

/**
 * Interface for ProductProviderDto - used for assign/unassign operations.
 * Moved from service-proxies.ts to prevent auto-generation overwrites.
 */
export interface IProductProviderDto {
	id: number;
	productId: number;
	providerId: number;
	grossPercentRateOverride: number | undefined;
	creationTime: moment.Moment;
}

/**
 * Data transfer object for product-provider relationships.
 * Moved from service-proxies.ts to prevent auto-generation overwrites.
 */
export class ProductProviderDto implements IProductProviderDto {
	id!: number;
	productId!: number;
	providerId!: number;
	grossPercentRateOverride!: number | undefined;
	creationTime!: moment.Moment;

	constructor(data?: IProductProviderDto) {
		if (data) {
			for (var property in data) {
				if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
			}
		}
	}

	init(_data?: any) {
		if (_data) {
			this.id = _data['id'];
			this.productId = _data['productId'];
			this.providerId = _data['providerId'];
			this.grossPercentRateOverride = _data['grossPercentRateOverride'];
			this.creationTime = _data['creationTime'] ? moment(_data['creationTime'].toString()) : <any>undefined;
		}
	}

	static fromJS(data: any): ProductProviderDto {
		data = typeof data === 'object' ? data : {};
		let result = new ProductProviderDto();
		result.init(data);
		return result;
	}

	toJSON(data?: any) {
		data = typeof data === 'object' ? data : {};
		data['id'] = this.id;
		data['productId'] = this.productId;
		data['providerId'] = this.providerId;
		data['grossPercentRateOverride'] = this.grossPercentRateOverride;
		data['creationTime'] = this.creationTime ? this.creationTime.toISOString() : <any>undefined;
		return data;
	}

	clone(): ProductProviderDto {
		const json = this.toJSON();
		let result = new ProductProviderDto();
		result.init(json);
		return result;
	}
}

@Injectable()
export class ProviderProductsService extends BaseService {
	constructor(private readonly _httpClient: HttpClient, protected readonly injector: Injector) {
		super(injector);
	}

	/**
	 * Gets a provider-product by ID
	 * @param providerProductId The ID of the provider-product to retrieve
	 * @returns Observable of ProviderProduct
	 */
	getProviderProduct(providerProductId: number): Observable<ProviderProduct> {
		let url = this.environment.backendUrl + `/api/services/app/ProviderProducts/v2/${providerProductId}`;

		url = url.replace(/[?&]$/, "");

		return this._httpClient
			.get<ProviderProduct>(url, {})
			.pipe(
				map((response: any) => {
					return response.result as ProviderProduct ?? null;
				}),
				catchError(this.handleError),
			);
	}

	/**
	 * Updates the gross rate percentage override for a specific provider-product combination.
	 *
	 * When the provided rate exactly matches the provider's default rate, the system
	 * automatically stores NULL to avoid redundant data.
	 *
	 * When NULL is provided, the override is cleared and the provider's default rate
	 * will be used for commission calculations.
	 *
	 * @param providerProductId The ID of the provider-product to update
	 * @param grossPercentRateOverride The gross rate percentage override (0-100). NULL to clear override.
	 * @returns Observable of updated ProviderProduct
	 */
	updateProviderProductGrossRate(
		providerProductId: number,
		grossPercentRateOverride: number | null
	): Observable<ProviderProduct> {
		let url = this.environment.backendUrl + `/api/services/app/ProviderProducts/v2/${providerProductId}/gross-rate`;

		url = url.replace(/[?&]$/, "");

		const body: UpdateProviderProductGrossRateRequest = {
			grossPercentRateOverride: grossPercentRateOverride
		};

		return this._httpClient
			.patch<ProviderProduct>(url, body, {})
			.pipe(
				map((response: any) => {
					return response.result as ProviderProduct ?? null;
				}),
				catchError(this.handleError),
			);
	}

	/**
	 * Assigns a product to a provider, creating a new provider-product relationship.
	 * Moved from service-proxies.ts to prevent auto-generation overwrites.
	 *
	 * @param providerId The ID of the provider to assign the product to.
	 * @param productId The ID of the product to assign.
	 * @returns Observable of the created ProductProviderDto.
	 */
	assignProductToProvider(providerId: number, productId: number): Observable<ProductProviderDto> {
		if (providerId === undefined || providerId === null) {
			throw new Error("The parameter 'providerId' must be defined.");
		}

		let url = this.environment.backendUrl + `/api/v2/providers/${encodeURIComponent('' + providerId)}/products`;

		url = url.replace(/[?&]$/, "");

		const body = { productId: productId };

		return this._httpClient
			.post<any>(url, body, {})
			.pipe(
				map((response: any) => {
					return response.providerProduct ? ProductProviderDto.fromJS(response.providerProduct) : new ProductProviderDto();
				}),
				catchError(this.handleError),
			);
	}

	/**
	 * Unassigns a product from a provider, removing the provider-product relationship.
	 * Moved from service-proxies.ts to prevent auto-generation overwrites.
	 *
	 * @param providerId The ID of the provider to unassign the product from.
	 * @param productId The ID of the product to unassign.
	 * @returns Observable of void.
	 */
	unassignProductFromProvider(providerId: number, productId: number): Observable<void> {
		if (providerId === undefined || providerId === null) {
			throw new Error("The parameter 'providerId' must be defined.");
		}
		if (productId === undefined || productId === null) {
			throw new Error("The parameter 'productId' must be defined.");
		}

		let url = this.environment.backendUrl + `/api/v2/providers/${encodeURIComponent('' + providerId)}/products/${encodeURIComponent('' + productId)}`;

		url = url.replace(/[?&]$/, "");

		return this._httpClient
			.delete<void>(url, {})
			.pipe(
				map(() => {
					return;
				}),
				catchError(this.handleError),
			);
	}
}

